import { Tier } from '@mondaygpt/constants'
import { createAsyncThunk, createSelector, createSlice, PayloadAction } from '@reduxjs/toolkit'
import { CustomerInfo, Offering, Package } from '@revenuecat/purchases-js'
import { OpenAPI } from 'openapi'
import { RootState } from 'types'
import { logout } from '../../authentication/slices/authentication-view.slice'
import { selectCurrentUser, selectCurrentUserId } from '../../common'
import { RevenueCatService } from '../services'
import { selectHasActiveSubscription } from './credit-balance.slice'

export type PaywallSource =
  | 'onboarding'
  | 'billing_page'
  | 'welcome_screen'
  | 'chat_header'
  | 'limit_reached'
  | 'message_action'
  | 'subscription_gate'
  | 'send_message'
  | 'download'
  | 'publish_instagram'
  | 'publish_facebook'
  | 'publish_linkedin'
  | 'publish_pinterest'
  | 'publish_tiktok'
  | 'publish_youtube'
  | 'publish_twitter'
  | 'create_creative'
  | 'duplicate_creative'
  | null

/**
 * Where a top-up push was triggered from. Mirrors `PaywallSource` so the
 * `TOPUP_MODAL_VIEWED` analytics can attribute the surface (sidebar vs. the
 * generation gate vs. the low-credit toast vs. the billing page).
 */
export type TopUpSource =
  | 'billing_page'
  | 'sidebar_empty'
  | 'top_banner'
  | 'generate_gate'
  | 'generation_failed'
  | 'chat_blocked'
  | null

export interface AppliedPromo {
  code: string
  campaign?: string
}

export interface RevenueCatState {
  isConfigured: boolean
  isLoading: boolean
  customerInfo: CustomerInfo | null
  currentOffering: Offering | null
  /** Discount-promo offering; when set the paywall renders it instead of currentOffering. */
  promoOffering: Offering | null
  appliedPromo: AppliedPromo | null
  error: string | null
  showPaywall: boolean
  paywallSource: PaywallSource
  showTopUpModal: boolean
  topUpSource: TopUpSource
}

const initialState: RevenueCatState = {
  isConfigured: false,
  isLoading: false,
  customerInfo: null,
  currentOffering: null,
  promoOffering: null,
  appliedPromo: null,
  error: null,
  showPaywall: false,
  paywallSource: null,
  showTopUpModal: false,
  topUpSource: null,
}

const safeParse = (value: any) => JSON.parse(JSON.stringify(value))

// Async thunks
export const initializeRevenueCat = createAsyncThunk(
  'revenueCat/initialize',
  async (_, { getState, rejectWithValue }) => {
    try {
      const state = getState() as RootState

      const currentUserId = selectCurrentUserId(state)
      if (!currentUserId) {
        throw new Error('User not authenticated')
      }

      const apiKey = import.meta.env.VITE_REVENUECAT_API_KEY
      if (!apiKey) {
        throw new Error('RevenueCat API key not configured')
      }

      const revenueCatService = RevenueCatService.getInstance()
      await revenueCatService.configure(apiKey, currentUserId)

      // Get initial customer info and offerings
      const [customerInfo, offering] = await Promise.all([
        revenueCatService.getCustomerInfo(),
        revenueCatService.getCurrentOffering(),
      ])

      return {
        customerInfo: safeParse(customerInfo),
        offering: offering ? safeParse(offering) : null,
      }
    } catch (error) {
      const errorMessage =
        error instanceof Error ? error.message : 'Failed to initialize RevenueCat'
      console.error('RevenueCat initialization error:', error)
      return rejectWithValue(errorMessage)
    }
  }
)

async function devGrantSubscription(productVendorId: string): Promise<void> {
  const token = typeof OpenAPI.TOKEN === 'function' ? await OpenAPI.TOKEN({} as any) : OpenAPI.TOKEN
  await fetch(`${OpenAPI.BASE}/v1/billing/dev/grant-subscription`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
    body: JSON.stringify({ productVendorId }),
  })
}

/**
 * Swaps the paywall to a discount offering after a promo-code redemption.
 * Rejects when the offering id doesn't exist in RevenueCat (admin
 * misconfiguration) so the paywall keeps the default offering.
 */
export const applyPromoOffering = createAsyncThunk(
  'revenueCat/applyPromoOffering',
  async (params: { offeringId: string; code: string; campaign?: string }, { rejectWithValue }) => {
    try {
      const revenueCatService = RevenueCatService.getInstance()
      const offering = await revenueCatService.getOfferingById(params.offeringId)
      if (!offering) {
        console.error('[applyPromoOffering] Offering not found in RevenueCat:', params.offeringId)
        return rejectWithValue('offering_not_found')
      }
      return {
        offering: safeParse(offering),
        code: params.code,
        campaign: params.campaign,
      }
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to load promo offering'
      return rejectWithValue(errorMessage)
    }
  }
)

export const purchasePackage = createAsyncThunk(
  'revenueCat/purchase',
  async (params: { packageToPurchase: Package }, { getState, rejectWithValue }) => {
    try {
      const state = getState() as RootState

      const currentUser = selectCurrentUser(state)
      if (!currentUser) {
        throw new Error('User not authenticated')
      }

      if (!state.revenueCat.isConfigured) {
        throw new Error('RevenueCat not configured')
      }

      const revenueCatService = RevenueCatService.getInstance()
      const updatedCustomerInfo = await revenueCatService.purchase(
        params.packageToPurchase,
        currentUser.email
      )
      if (!updatedCustomerInfo) return rejectWithValue('__cancelled__')

      if (import.meta.env.DEV) {
        const vendorId = params.packageToPurchase.webBillingProduct?.identifier
        if (vendorId) {
          devGrantSubscription(vendorId).catch(err =>
            console.warn('[DEV] Failed to grant direct subscription:', err)
          )
        }
      }

      return safeParse(updatedCustomerInfo)
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Purchase failed'
      return rejectWithValue(errorMessage)
    }
  }
)

const revenueCatSlice = createSlice({
  name: 'revenueCat',
  initialState,
  reducers: {
    openPaywall: (
      state,
      action: PayloadAction<{
        source: PaywallSource
      }>
    ) => {
      state.showPaywall = true
      state.paywallSource = action.payload.source
    },
    closePaywall: state => {
      state.showPaywall = false
      state.paywallSource = null
    },
    openTopUpModal: (state, action: PayloadAction<{ source?: TopUpSource } | undefined>) => {
      state.showTopUpModal = true
      state.topUpSource = action?.payload?.source ?? 'billing_page'
    },
    closeTopUpModal: state => {
      state.showTopUpModal = false
      state.topUpSource = null
    },
    /** Undo an applied promo: the plan picker falls back to the default offering. */
    clearPromoOffering: state => {
      state.promoOffering = null
      state.appliedPromo = null
    },
  },
  extraReducers: builder => {
    builder.addCase(logout, () => initialState)

    builder
      .addCase(initializeRevenueCat.pending, state => {
        state.isLoading = true
        state.error = null
      })
      .addCase(initializeRevenueCat.fulfilled, (state, action) => {
        state.isConfigured = true
        state.isLoading = false
        state.customerInfo = action.payload.customerInfo
        state.currentOffering = action.payload.offering
        state.error = null
      })
      .addCase(initializeRevenueCat.rejected, (state, action) => {
        state.isLoading = false
        state.error = action.payload as string
      })
      .addCase(applyPromoOffering.fulfilled, (state, action) => {
        state.promoOffering = action.payload.offering
        state.appliedPromo = { code: action.payload.code, campaign: action.payload.campaign }
      })
      .addCase(purchasePackage.pending, state => {
        state.isLoading = true
        state.error = null
      })
      .addCase(purchasePackage.fulfilled, (state, action) => {
        state.isLoading = false
        state.customerInfo = action.payload
        state.error = null
      })
      .addCase(purchasePackage.rejected, (state, action) => {
        state.isLoading = false
        if (action.payload !== '__cancelled__') {
          state.error = action.payload as string
        }
      })
  },
})

export const revenueCatReducer = revenueCatSlice.reducer
export const { openPaywall, closePaywall, openTopUpModal, closeTopUpModal, clearPromoOffering } =
  revenueCatSlice.actions

// Selectors
export const selectRevenueCat = (state: RootState) => state.revenueCat

export const selectRevenueCatConfig = (state: RootState) => ({
  isConfigured: state.revenueCat.isConfigured,
  isLoading: state.revenueCat.isLoading,
  error: state.revenueCat.error,
})

export const selectCustomerInfo = (state: RootState) => state.revenueCat.customerInfo

const selectRcShowUpgrade = createSelector([selectCustomerInfo], customerInfo => {
  if (!customerInfo) return true
  const active = customerInfo.entitlements?.active
  return !(active?.[Tier.Pro]?.isActive || active?.[Tier.Business]?.isActive)
})

export const selectShowUpgrade = createSelector(
  [selectRcShowUpgrade, selectHasActiveSubscription],
  (rcShowUpgrade, hasActiveSubscription) => {
    if (hasActiveSubscription) return false
    return rcShowUpgrade
  }
)

export const selectCurrentOffering = (state: RootState) => state.revenueCat.currentOffering

export const selectPromoOffering = (state: RootState) => state.revenueCat.promoOffering

export const selectAppliedPromo = (state: RootState) => state.revenueCat.appliedPromo

const selectShowPaywall = (state: RootState) => state.revenueCat.showPaywall
const selectPaywallSource = (state: RootState) => state.revenueCat.paywallSource

export const selectPaywallState = createSelector(
  [selectShowPaywall, selectPaywallSource],
  (showPaywall, source) => ({
    showPaywall,
    source,
  })
)

export const selectShowTopUpModal = (state: RootState) => state.revenueCat.showTopUpModal

export const selectTopUpSource = (state: RootState) => state.revenueCat.topUpSource
